import React, { RefObject, useState, useEffect, useRef, useMemo } from 'react';
import {
  Globe,
  MessageSquare,
  Paperclip,
  Plug,
  CornerDownLeft,
  X,
  Sparkles,
  ChevronDown,
  Check,
} from 'lucide-react';
import { Document } from './DocumentPillList';
import { ExecutionDocument } from '../../../../../shared/services/documentService';
import { MentionAutocomplete, MentionSuggestion } from './MentionAutocomplete';
import { KnowledgeBaseWithDocuments } from '../../../../../types/navigationTypes';
import { uploadDocumentToKnowledgeBase } from '../../../../knowledgebase/services/documentUploadService';
import { toast } from 'sonner';
import { createMentionPill, createExecutionTemplatePill } from './MentionPillFactory';
import { ChatPillData, type EnabledConnectors } from '../../../types/chatTypes';
import { fetchExecutionTemplatesForExecution } from '../../../services/executionTemplatesChatService';
import type { ExecutionTemplateForChat } from '../../../services/executionTemplatesChatService';
import { configService } from '../../../../../shared/services/configService';
import {
  EXECUTION_TEMPLATES_CHANGED_EVENT,
  KB_DOCUMENTS_CHANGED_EVENT,
} from '../../../../projects/services/executionTemplatesEvents';
import { CHAT_CONNECTOR_DEFINITIONS } from '../../../config/connectors-config';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Same accept list as web chat */
export const CHAT_ATTACHMENT_ACCEPT = '.txt,.pdf,.doc,.docx,.xls,.xlsx,.csv';

interface ChatInputAreaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onAttachKnowledgeBaseDocument: (doc: ExecutionDocument, isCategory?: boolean) => void;
  documents: Document[];
  onRemoveDocument: (id: string) => void;
  loading?: boolean;
  disabled?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  knowledgeBaseDocuments?: ExecutionDocument[];
  isLoadingKbDocs?: boolean;
  onLoadDocuments?: () => void;
  onRefetchDocuments?: () => void;
  isAgentMode?: boolean;
  onToggleAgentMode?: () => void;
  isTestMode?: boolean;
  onToggleTestMode?: () => void;
  isWebSearchMode?: boolean;
  onToggleWebSearchMode?: () => void;
  onStop?: () => void;
  knowledgeBases?: KnowledgeBaseWithDocuments[];
  onUploadSuccess?: () => void;
  onOpenConnectors?: () => void;
  enabledConnectors?: EnabledConnectors;
  availableConnectors?: EnabledConnectors;
  workpillExecutionId?: string | null;
}

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  onAttachKnowledgeBaseDocument,
  documents: _documents,
  onRemoveDocument: _onRemoveDocument,
  loading = false,
  disabled = false,
  inputRef: _inputRef,
  placeholder = "Type your message...",
  knowledgeBaseDocuments = [],
  isLoadingKbDocs = false,
  onLoadDocuments,
  onRefetchDocuments,
  isAgentMode = false,
  onToggleAgentMode,
  isTestMode = false,
  onToggleTestMode,
  isWebSearchMode = false,
  onToggleWebSearchMode,
  onStop,
  knowledgeBases = [],
  onUploadSuccess,
  onOpenConnectors,
  enabledConnectors,
  availableConnectors,
  workpillExecutionId = null,
}) => {
  const [executionTemplates, setExecutionTemplates] = useState<ExecutionTemplateForChat[]>([]);
  const [kbPickerOpen, setKbPickerOpen] = useState(false);
  const [pendingChatFiles, setPendingChatFiles] = useState<File[]>([]);
  const [pickerKbId, setPickerKbId] = useState<string | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);

  /**
   * Project-scoped upload targets (exclude org shared KBs).
   * Use `!== true` so null/undefined rows still allow upload; only explicit `is_global: true` is read-only.
   */
  const chatUploadTargets = useMemo(
    () => (knowledgeBases || []).filter((kb) => kb.is_global !== true),
    [knowledgeBases]
  );
  const chatAttachmentsEnabled = chatUploadTargets.length > 0;

  const finishChatUploadAndNotify = React.useCallback(
    (uploadedDocs: { id: string; name: string }[]) => {
      uploadedDocs.forEach((doc) =>
        onAttachKnowledgeBaseDocument({ id: doc.id, name: doc.name, type: 'document' }, false)
      );
      onUploadSuccess?.();
      onLoadDocuments?.();
      onRefetchDocuments?.();
    },
    [onAttachKnowledgeBaseDocument, onUploadSuccess, onLoadDocuments, onRefetchDocuments]
  );

  const runChatUploadToKb = React.useCallback(
    async (files: File[], kbId: string): Promise<{ id: string; name: string }[]> => {
      const uploaded: { id: string; name: string }[] = [];
      for (const file of files) {
        const result = await uploadDocumentToKnowledgeBase({
          file,
          knowledgeBaseId: kbId,
          folderId: null,
          documentName: file.name,
        });
        if (!result.success || !result.documentId) {
          throw new Error(result.error || `Failed to upload ${file.name}`);
        }
        uploaded.push({ id: result.documentId, name: file.name });
      }
      finishChatUploadAndNotify(uploaded);
      return uploaded;
    },
    [finishChatUploadAndNotify]
  );

  const handleChatFilesSelected = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      if (chatUploadTargets.length === 0) {
        toast.error('No knowledge base available', {
          description: 'Add a project knowledge base to upload documents from chat.',
        });
        return;
      }
      if (chatUploadTargets.length === 1) {
        const tid = toast.loading('Uploading...');
        runChatUploadToKb(files, chatUploadTargets[0].id)
          .then(() => toast.success('Upload complete', { id: tid }))
          .catch((e) =>
            toast.error(e instanceof Error ? e.message : 'Upload failed', { id: tid })
          );
        return;
      }
      setPendingChatFiles(files);
      setPickerKbId(chatUploadTargets[0]?.id ?? null);
      setKbPickerOpen(true);
    },
    [chatUploadTargets, runChatUploadToKb]
  );

  const confirmKbPickerUpload = React.useCallback(async () => {
    if (!pickerKbId || pendingChatFiles.length === 0) return;
    setKbPickerOpen(false);
    const files = pendingChatFiles;
    const kbId = pickerKbId;
    setPendingChatFiles([]);
    setPickerKbId(null);
    const tid = toast.loading('Uploading...');
    try {
      await runChatUploadToKb(files, kbId);
      toast.success('Upload complete', { id: tid });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed', { id: tid });
    }
  }, [pickerKbId, pendingChatFiles, runChatUploadToKb]);

  const closeKbPicker = React.useCallback(() => {
    setKbPickerOpen(false);
    setPendingChatFiles([]);
    setPickerKbId(null);
  }, []);

  const activeConnectorLogos = useMemo(() => {
    if (!enabledConnectors || !availableConnectors) return [];
    return CHAT_CONNECTOR_DEFINITIONS.filter(
      (c) => availableConnectors[c.id] && enabledConnectors[c.id]
    );
  }, [enabledConnectors, availableConnectors]);

  const hasActiveConnectors = activeConnectorLogos.length > 0;

  const fetchTemplates = React.useCallback(() => {
    if (!workpillExecutionId) {
      setExecutionTemplates([]);
      return;
    }
    fetchExecutionTemplatesForExecution(workpillExecutionId)
      .then((rows) => setExecutionTemplates(rows))
      .catch(() => setExecutionTemplates([]));
  }, [workpillExecutionId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    window.addEventListener(EXECUTION_TEMPLATES_CHANGED_EVENT, fetchTemplates);
    return () => window.removeEventListener(EXECUTION_TEMPLATES_CHANGED_EVENT, fetchTemplates);
  }, [fetchTemplates]);

  useEffect(() => {
    const handleKbChanged = () => {
      if (onRefetchDocuments) onRefetchDocuments();
    };
    window.addEventListener(KB_DOCUMENTS_CHANGED_EVENT, handleKbChanged);
    return () => window.removeEventListener(KB_DOCUMENTS_CHANGED_EVENT, handleKbChanged);
  }, [onRefetchDocuments]);

  const executionTemplateSuggestions = useMemo((): MentionSuggestion[] => {
    return executionTemplates.map((et) => ({
      id: et.id,
      name: et.name,
      type: 'execution_template',
      templateType: et.template_type,
    }));
  }, [executionTemplates]);
  
  const [mentionVisible, setMentionVisible] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionSuggestion[]>([]);
  const [allMentionSuggestions, setAllMentionSuggestions] = useState<MentionSuggestion[]>([]);
  
  const editableRef = useRef<HTMLDivElement>(null);

  const organizeDocumentsForMention = (docs: ExecutionDocument[]): MentionSuggestion[] => {
    const suggestions: MentionSuggestion[] = [];
    
    const docsByKB: Record<string, { name: string; docs: ExecutionDocument[] }> = {};
    
    docs.forEach(doc => {
      if (doc.knowledge_base_name && doc.metadata?.knowledge_base_id) {
        const kbId = doc.metadata.knowledge_base_id;
        if (!docsByKB[kbId]) {
          docsByKB[kbId] = {
            name: doc.knowledge_base_name,
            docs: []
          };
        }
        docsByKB[kbId].docs.push(doc);
      } 
      else {
        suggestions.push({
          id: doc.id,
          name: doc.name,
          type: 'document',
          document: doc,
          icon: null
        });
      }
    });
    
    Object.entries(docsByKB).forEach(([kbId, data]) => {
      suggestions.push({
        id: `kb-${kbId}`,
        name: data.name,
        type: 'knowledge_base',
        knowledgeBaseId: kbId,
        knowledgeBaseName: data.name
      });
      
      data.docs.forEach(doc => {
        suggestions.push({
          id: doc.id,
          name: doc.name,
          type: 'document',
          document: doc,
          knowledgeBaseId: kbId,
          knowledgeBaseName: data.name
        });
      });
    });
    
    return suggestions;
  };

  useEffect(() => {
    const fromDocs =
      knowledgeBaseDocuments.length > 0
        ? organizeDocumentsForMention(knowledgeBaseDocuments)
        : [];
    setAllMentionSuggestions([...executionTemplateSuggestions, ...fromDocs]);
  }, [knowledgeBaseDocuments, executionTemplateSuggestions]);

  useEffect(() => {
    if (!mentionQuery) {
      setMentionSuggestions(allMentionSuggestions);
    } else {
      const lowerQuery = mentionQuery.toLowerCase();
      const filtered = allMentionSuggestions.filter(item => 
        item.name.toLowerCase().includes(lowerQuery) || 
        (item.knowledgeBaseName && item.knowledgeBaseName.toLowerCase().includes(lowerQuery))
      );
      setMentionSuggestions(filtered);
    }
    setMentionSelectedIndex(0);
  }, [mentionQuery, allMentionSuggestions]);

  // Función auxiliar para procesar el contenido y extraer UUIDs de las pills
  const getProcessedText = (root: HTMLElement): string => {
    let text = '';
    const nodes = root.childNodes;
    
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node instanceof HTMLElement) {
        if (node.getAttribute('data-type') === 'execution_template') {
          const id = node.getAttribute('data-execution-template-id');
          const name = (node.getAttribute('data-label') || '').replace(/\]/g, '');
          const tt = node.getAttribute('data-template-type') || '';
          const suffix = tt === 'word' || tt === 'excel' ? `:${tt}` : '';
          if (id) {
            text += `@[${name}](execution_template:${id}${suffix})`;
          }
        } else if (node.hasAttribute('data-document-id')) {
          const id = node.getAttribute('data-document-id');
          const rawLabel =
            node.getAttribute('data-label') || node.getAttribute('data-document-name') || '';
          const label = rawLabel.replace(/\]/g, '');
          const mentionType = node.getAttribute('data-type') || 'document';
          if (id) {
            text += `@[${label}](${mentionType}:${id})`;
          }
        } else {
          text += getProcessedText(node);
        }
      }
    }
    return text;
  };

  const updateValueFromContent = () => {
    if (!editableRef.current) return;
    
    // Usar la función procesada que extrae UUIDs de las pills
    const processedText = getProcessedText(editableRef.current);
    
    const syntheticEvent = {
      target: {
        value: processedText,
        name: '',
        type: 'textarea'
      },
      currentTarget: {
        value: processedText,
        name: '',
        type: 'textarea'
      },
      preventDefault: () => {},
      stopPropagation: () => {}
    } as React.ChangeEvent<HTMLTextAreaElement>;
    
    onChange(syntheticEvent);
  };

  const handleContentEditableInput = () => {
    updateValueFromContent();
    
    // Sincronizar documentos visuales (pills) con el estado
    if (editableRef.current) {
      const pillNodes = editableRef.current.querySelectorAll('[data-document-id]');
      const currentPillIds = new Set<string>();
      
      pillNodes.forEach(node => {
          const id = node.getAttribute('data-document-id');
          if (id) currentPillIds.add(id);
      });
      
      // Si tenemos documentos en el estado que ya no están en el DOM, los eliminamos
      // Esto maneja el caso de borrar con Backspace/Delete
      if (_documents && _documents.length > 0) {
        _documents.forEach(doc => {
          if (!currentPillIds.has(doc.id)) {
            console.log('🗑️ Sync: Removing document missing from DOM:', doc.name);
            _onRemoveDocument(doc.id);
          }
        });
      }
    }
    
    if (!editableRef.current) return;
    const editableDiv = editableRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const textContent = editableDiv.textContent || '';
    
    let cursorOffset = 0;
    const walker = document.createTreeWalker(
      editableDiv,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let node;
    while (node = walker.nextNode()) {
      if (node === range.startContainer) {
        cursorOffset += range.startOffset;
        break;
      }
      cursorOffset += node.textContent?.length || 0;
    }
    
    const textBeforeCursor = textContent.substring(0, cursorOffset);
    const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbolIndex !== -1) {
      const charBeforeAt = lastAtSymbolIndex > 0 ? textContent[lastAtSymbolIndex - 1] : ' ';
      const isValidAt = /[\s\n]/.test(charBeforeAt);
      
      if (isValidAt) {
        const query = textBeforeCursor.substring(lastAtSymbolIndex + 1);
        if (!query.includes(' ')) {
          setMentionQuery(query);
          setMentionVisible(true);
          
          const { top, left } = editableDiv.getBoundingClientRect();
          setMentionPosition({ 
            top: top - 320,
            left: left + 10 
          });
          
          if (!isLoadingKbDocs) {
            if (knowledgeBaseDocuments.length === 0 && onLoadDocuments) {
              onLoadDocuments();
            } else if (onRefetchDocuments) {
              onRefetchDocuments();
            }
          }
          return;
        }
      }
    }
    
    setMentionVisible(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mentionVisible && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex(prev => (prev + 1) % mentionSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex(prev => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSelectSuggestion(mentionSuggestions[mentionSelectedIndex]);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        handleSelectSuggestion(mentionSuggestions[mentionSelectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionVisible(false);
        return;
      }
    }
    
    if (e.key === 'Enter' && !e.shiftKey && !mentionVisible) {
      e.preventDefault();
      onSend();
      return;
    }
    
    if (onKeyPress) {
      const syntheticEvent = {
        ...e,
        target: e.target as any,
        currentTarget: e.currentTarget as any
      } as any;
      onKeyPress(syntheticEvent);
    }
  };

  const handleSelectSuggestion = (suggestion: MentionSuggestion) => {
    console.log('🎯 Selected suggestion:', suggestion);
    if (!editableRef.current) return;
    
    const editableDiv = editableRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const textContent = editableDiv.textContent || '';
    
    let cursorOffset = 0;
    const walker = document.createTreeWalker(
      editableDiv,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let node;
    let atNode: Node | null = null;
    let atOffset = 0;
    let cursorNode: Node | null = null;
    let cursorOffsetInNode = 0;
    
    while (node = walker.nextNode()) {
      if (node === range.startContainer) {
        cursorNode = node;
        cursorOffsetInNode = range.startOffset;
        cursorOffset += range.startOffset;
        break;
      }
      cursorOffset += node.textContent?.length || 0;
    }
    
    const textBeforeCursor = textContent.substring(0, cursorOffset);
    const lastAtSymbolIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbolIndex !== -1) {
      let charCount = 0;
      const walker2 = document.createTreeWalker(
        editableDiv,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      while (node = walker2.nextNode()) {
        const nodeLength = node.textContent?.length || 0;
        if (charCount + nodeLength >= lastAtSymbolIndex) {
          atNode = node;
          atOffset = lastAtSymbolIndex - charCount;
          break;
        }
        charCount += nodeLength;
      }
      
      if (atNode && cursorNode) {
        const newRange = document.createRange();
        newRange.setStart(atNode, atOffset);
        newRange.setEnd(cursorNode, cursorOffsetInNode);
        
        newRange.deleteContents();

        let pillSpan: HTMLElement;
        if (suggestion.type === 'execution_template') {
          pillSpan = createExecutionTemplatePill(
            {
              id: suggestion.id,
              name: suggestion.name,
              templateType: suggestion.templateType,
            },
            () => {
              updateValueFromContent();
            }
          );
          configService.setActiveExecutionTemplateId(suggestion.id);
        } else {
          pillSpan = createMentionPill(suggestion, () => {
            updateValueFromContent();
            const docId = suggestion.document?.id || suggestion.id;
            _onRemoveDocument(docId);
          });
        }

        newRange.insertNode(pillSpan);

        const spaceNode = document.createTextNode(' ');
        pillSpan.parentNode?.insertBefore(spaceNode, pillSpan.nextSibling);

        const newRange2 = document.createRange();
        newRange2.setStartAfter(spaceNode);
        newRange2.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange2);

        updateValueFromContent();

        if (suggestion.type === 'execution_template') {
          // Active template already set above; not a KB attachment
        } else if (suggestion.type === 'document' && suggestion.document) {
          onAttachKnowledgeBaseDocument(suggestion.document, false);
        } else if (suggestion.type === 'knowledge_base' || suggestion.type === 'folder') {
          const doc: ExecutionDocument = {
            id: suggestion.id,
            name: suggestion.name,
            type: 'knowledge_base',
            category: suggestion.type === 'knowledge_base' ? 'Knowledge Base' : 'Folder',
            metadata: {
              knowledge_base_id: suggestion.knowledgeBaseId,
            },
          };
          onAttachKnowledgeBaseDocument(doc, true);
        } else if (suggestion.type !== 'execution_template') {
          console.warn('⚠️ Suggestion type not handled or missing document:', suggestion);
        }
        
        setMentionVisible(false);
        editableDiv.focus();
      }
    }
  };

  const handleSelectDocument = (doc: ExecutionDocument, isCategory: boolean = false) => {
    onAttachKnowledgeBaseDocument(doc, isCategory);
  };

  // Helper para buscar documento por ID para restaurar pills
  /** Same token shape as web chat: @[label](type:id[:word|excel]?) */
  const AT_MENTION_TOKEN =
    /^@\[([^\]]*)\]\(([^:]+):([^:)]+)(?::(word|excel))?\)$/;

  const findDocumentById = (id: string): ChatPillData | null => {
    // Buscar en knowledgeBaseDocuments (prioridad alta, son los docs disponibles)
    const kbDoc = knowledgeBaseDocuments.find(d => d.id === id);
    if (kbDoc) {
       return {
         id: kbDoc.id,
         name: kbDoc.name,
         type: 'document',
         isCategory: false,
         knowledgeBaseId: kbDoc.metadata?.knowledge_base_id,
         knowledgeBaseName: kbDoc.knowledge_base_name
       };
    }

    // Buscar folders/kbs en knowledgeBases
    for (const kb of knowledgeBases) {
       if (kb.id === id || `kb-${kb.id}` === id) {
          return {
             id: `kb-${kb.id}`,
             name: kb.name,
             type: 'knowledge_base',
             isCategory: true,
             knowledgeBaseId: kb.id,
             knowledgeBaseName: kb.name
          };
       }
    }
    
    // Fallback: Buscar en documentos adjuntos actuales (si aún existen en el estado)
    const attachedDoc = _documents.find(d => d.id === id);
    if (attachedDoc) {
      return {
        id: attachedDoc.id,
        name: attachedDoc.name,
        type: attachedDoc.isCategory ? 'category' : 'document',
        isCategory: attachedDoc.isCategory
      };
    }
    
    return null;
  };

  useEffect(() => {
    if (!editableRef.current) return;
    
    const currentProcessedText = getProcessedText(editableRef.current);
    if (currentProcessedText !== value) {
      if (!value) {
        editableRef.current.textContent = '';
        return;
      }

      const hasLegacyIds = value.includes('ID:');
      const hasAtMentions = /@\[[^\]]+\]\([^)]+\)/.test(value);
      if (hasLegacyIds || hasAtMentions) {
        editableRef.current.innerHTML = '';

        const appendPillForDoc = (doc: ChatPillData) => {
          const pill = createMentionPill(doc, () => {
            updateValueFromContent();
            _onRemoveDocument(doc.id);
          });
          editableRef.current?.appendChild(document.createTextNode('\u00A0'));
          editableRef.current?.appendChild(pill);
          editableRef.current?.appendChild(document.createTextNode('\u00A0'));
        };

        const tokenRe = /@\[[^\]]+\]\([^)]+\)|ID:[a-zA-Z0-9-]+/g;
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = tokenRe.exec(value)) !== null) {
          if (m.index > last) {
            editableRef.current.appendChild(
              document.createTextNode(value.slice(last, m.index))
            );
          }
          const token = m[0];
          if (token.startsWith('ID:')) {
            const id = token.slice(3);
            const doc = findDocumentById(id);
            if (doc) appendPillForDoc(doc);
            else editableRef.current.appendChild(document.createTextNode(token));
          } else {
            const parsed = AT_MENTION_TOKEN.exec(token);
            if (parsed) {
              const [, label, mentionType, id, tmplKind] = parsed;
              if (mentionType === 'execution_template') {
                const pill = createExecutionTemplatePill(
                  {
                    id,
                    name: label || 'Template',
                    templateType:
                      tmplKind === 'word' || tmplKind === 'excel' ? tmplKind : null,
                  },
                  () => updateValueFromContent()
                );
                editableRef.current.appendChild(document.createTextNode('\u00A0'));
                editableRef.current.appendChild(pill);
                editableRef.current.appendChild(document.createTextNode('\u00A0'));
              } else {
                const resolved = findDocumentById(id);
                const doc: ChatPillData =
                  resolved ||
                  ({
                    id,
                    name: label || id,
                    type: mentionType,
                    isCategory:
                      mentionType === 'knowledge_base' ||
                      mentionType === 'category' ||
                      mentionType === 'folder',
                  } as ChatPillData);
                appendPillForDoc(doc);
              }
            } else {
              editableRef.current.appendChild(document.createTextNode(token));
            }
          }
          last = m.index + token.length;
        }
        if (last < value.length) {
          editableRef.current.appendChild(document.createTextNode(value.slice(last)));
        }

        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        return;
      }

      const selection = window.getSelection();
      let cursorOffset = 0;
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const walker = document.createTreeWalker(
          editableRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );
        let node;
        while (node = walker.nextNode()) {
          if (node === range.startContainer) {
            cursorOffset += range.startOffset;
            break;
          }
          cursorOffset += node.textContent?.length || 0;
        }
      }
      
      editableRef.current.textContent = value;
      
      if (selection && cursorOffset > 0) {
        const walker = document.createTreeWalker(
          editableRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );
        let node;
        let charCount = 0;
        while (node = walker.nextNode()) {
          const nodeLength = node.textContent?.length || 0;
          if (charCount + nodeLength >= cursorOffset) {
            const range = document.createRange();
            range.setStart(node, cursorOffset - charCount);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            break;
          }
          charCount += nodeLength;
        }
      }
    }
  }, [value]);

  useEffect(() => {
    const editableDiv = editableRef.current;
    if (!editableDiv) return;

    editableDiv.style.height = 'auto';

    const scrollHeight = editableDiv.scrollHeight;
    const minH = 56;
    const maxH = 200;
    const newHeight = Math.min(Math.max(scrollHeight, minH), maxH);
    editableDiv.style.height = `${newHeight}px`;
    editableDiv.style.overflowY = scrollHeight > maxH ? 'auto' : 'hidden';
  }, [value]);

  const showFloatingPlaceholder = !value || value.trim() === '';
  const canSend = value.trim().length > 0 && !disabled && !loading;

  return (
    <div className="relative w-full z-10 px-3 py-2 group/input">
      <div
        className={cn(
          'relative w-full bg-white/80 backdrop-blur-sm border border-gray-200/80 rounded-lg transition-all duration-200 shadow-sm',
          'focus-within:border-gray-300/90 focus-within:shadow-md',
          (disabled || loading) && 'opacity-90'
        )}
      >
        <div className="relative">
          {showFloatingPlaceholder && (
            <div className="absolute top-4 left-5 text-gray-400 pointer-events-none text-[15px] select-none leading-relaxed z-0">
              {placeholder}
            </div>
          )}
          <div
            ref={editableRef}
            contentEditable={!disabled && !loading}
            onInput={handleContentEditableInput}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              document.execCommand('insertText', false, text);
            }}
            suppressContentEditableWarning={true}
            className={cn(
              'relative w-full bg-transparent text-gray-900 resize-none text-[15px] leading-relaxed',
              'min-h-[56px] max-h-[200px] py-4 pl-5 pr-4 overflow-y-auto z-10',
              'border-none outline-none whitespace-pre-wrap break-words cursor-text',
              'focus:outline-none focus-visible:ring-0'
            )}
            style={{ wordWrap: 'break-word' }}
            role="textbox"
            aria-multiline="true"
          />
        </div>

        <div className="flex flex-wrap justify-between items-center px-4 py-2 gap-2 border-t border-gray-100/80">
          <input
            ref={attachInputRef}
            type="file"
            className="hidden"
            multiple
            accept={CHAT_ATTACHMENT_ACCEPT}
            onChange={(e) => {
              const selected = Array.from(e.target.files || []);
              e.target.value = '';
              handleChatFilesSelected(selected);
            }}
          />
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            {onToggleAgentMode && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || loading}
                    className={cn(
                      'h-8 gap-1.5 px-2.5 text-[11px] font-medium shadow-sm border-gray-200/90 bg-white hover:bg-gray-50/90',
                      disabled || loading ? 'opacity-50' : ''
                    )}
                    title={isAgentMode ? 'Agent mode' : 'Ask mode'}
                  >
                    {isAgentMode ? (
                      <Sparkles size={12} strokeWidth={2} className="shrink-0 text-gray-600" />
                    ) : (
                      <MessageSquare size={12} strokeWidth={2} className="shrink-0 text-gray-600" />
                    )}
                    <span className="text-gray-900">{isAgentMode ? 'Agent' : 'Ask'}</span>
                    <ChevronDown size={14} strokeWidth={2} className="shrink-0 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="start"
                  className="w-[min(20rem,calc(100vw-2rem))] rounded-xl p-1"
                >
                  <DropdownMenuItem
                    className="items-start gap-2.5 py-2.5 px-3 cursor-pointer"
                    disabled={disabled || loading}
                    onClick={() => {
                      if (isAgentMode) onToggleAgentMode();
                    }}
                  >
                    <MessageSquare size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-gray-600" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-foreground">Ask</div>
                      <div className="text-[11px] leading-snug text-muted-foreground font-normal">
                        Get answers without running tools on your files.
                      </div>
                    </div>
                    {!isAgentMode && (
                      <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-foreground" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="items-start gap-2.5 py-2.5 px-3 cursor-pointer"
                    disabled={disabled || loading}
                    onClick={() => {
                      if (!isAgentMode) onToggleAgentMode();
                    }}
                  >
                    <Sparkles size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-gray-600" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-foreground">Agent</div>
                      <div className="text-[11px] leading-snug text-muted-foreground font-normal">
                        Use tools to read and edit documents in your project.
                      </div>
                    </div>
                    {isAgentMode && (
                      <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-foreground" />
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {onToggleWebSearchMode && (
              <button
                type="button"
                onClick={onToggleWebSearchMode}
                disabled={disabled || loading}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors border shrink-0',
                  isWebSearchMode
                   ? 'bg-blue-50 text-blue-600 border-blue-100'
                   : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-100 hover:text-gray-700',
                  disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
                )}
                title={isWebSearchMode ? 'Web search on' : 'Web search off'}
              >
                <Globe size={12} strokeWidth={2} />
                <span>Web</span>
              </button>
            )}

            {onOpenConnectors && (
              <button
                type="button"
                onClick={onOpenConnectors}
                disabled={disabled || loading}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors border shrink-0',
                  hasActiveConnectors
                   ? 'bg-blue-50 text-blue-600 border-blue-100'
                   : 'bg-transparent text-gray-500 border-transparent hover:bg-gray-100 hover:text-gray-700',
                  disabled || loading ? 'opacity-50 cursor-not-allowed' : ''
                )}
                title="Connectors"
              >
                <Plug size={12} strokeWidth={2} />
                <span className="shrink-0">Connectors</span>
                {hasActiveConnectors && (
                  <span className="flex items-center gap-0.5 shrink-0 pl-1.5 ml-0.5 border-l border-blue-200">
                    {activeConnectorLogos.map((c) => (
                      <img
                        key={c.id}
                        src={c.logoUrl}
                        alt=""
                        className="h-3.5 w-3.5 object-contain"
                        aria-hidden
                      />
                    ))}
                  </span>
                )}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                if (!chatAttachmentsEnabled || disabled || loading) return;
                attachInputRef.current?.click();
              }}
              disabled={disabled || loading || !chatAttachmentsEnabled}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              title={
                chatAttachmentsEnabled
                  ? 'Upload documents to knowledge base (root folder)'
                  : 'No project knowledge base available for uploads'
              }
            >
              <Paperclip size={16} strokeWidth={2} />
            </button>

            {onToggleTestMode && (
              <Button
                variant={isTestMode ? 'default' : 'outline'}
                size="sm"
                onClick={onToggleTestMode}
                className="h-6 px-2 text-[10px] shrink-0"
                title="Toggle Test"
              >
                TST
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {loading && onStop ? (
              <button
                type="button"
                onClick={onStop}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-[11px] font-medium bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 cursor-pointer"
                title="Cancel request"
              >
                <X size={12} strokeWidth={2} />
                <span>Cancel</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                className={cn(
                  'flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg transition-all font-medium text-[11px]',
                  canSend
                   ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer shadow-sm hover:shadow'
                   : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                )}
                title="Send (Enter)"
              >
                <span className="hidden min-[340px]:inline font-sans font-semibold text-[10px] bg-white/20 px-1 rounded mr-0.5">
                  Enter
                </span>
                <CornerDownLeft size={14} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      <MentionAutocomplete
        visible={mentionVisible}
        query={mentionQuery}
        suggestions={mentionSuggestions}
        selectedIndex={mentionSelectedIndex}
        onSelect={handleSelectSuggestion}
        onClose={() => setMentionVisible(false)}
        isLoading={isLoadingKbDocs}
      />
      
      <Dialog open={kbPickerOpen} onOpenChange={(open) => !open && closeKbPicker()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload to knowledge base</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Choose which project knowledge base should receive these files (root folder).
            </p>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-1 max-h-[50vh] overflow-y-auto">
            {chatUploadTargets.map((t) => (
              <label
                key={t.id}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:bg-muted/50"
              >
                <input
                  type="radio"
                  name="chat-kb-upload-target"
                  className="mt-1"
                  checked={pickerKbId === t.id}
                  onChange={() => setPickerKbId(t.id)}
                />
                <span className="text-sm font-medium text-foreground">{t.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={closeKbPicker}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmKbPickerUpload} disabled={!pickerKbId}>
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
